import { PropsWithChildren, ReactNode, useEffect } from 'react';

import {
  Box,
  Card,
  chakra,
  Collapse,
  createIcon,
  Flex,
  Heading,
  IconButton,
  Skeleton,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
  useRadio,
  useRadioGroup,
  UseRadioProps,
} from '@chakra-ui/react';

import { BillingFrequency } from '~/features/checkout/models/checkout';

import { ChevronDownIcon, ChevronUpIcon } from '../../ab_plp_redesign_2025/icons';

const RadioCheckedIcon = createIcon({
  displayName: 'RadioCheckedIcon',
  viewBox: '0 0 20 20',
  path: (
    <>
      <path
        d='M20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10Z'
        fill='#175CFF'
      />
      <path
        d='M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z'
        fill='white'
      />
    </>
  ),
});

const RadioUncheckedIcon = createIcon({
  displayName: 'RadioUncheckedIcon',
  viewBox: '0 0 20 20',
  path: (
    <>
      <rect fill='#F2F2F2' height='18.67' rx='9.335' width='18.67' x='0.665' y='0.665' />
      <rect
        fill='none'
        height='18.67'
        rx='9.335'
        stroke='#9C9FA8'
        strokeWidth='1.33'
        width='18.67'
        x='0.665'
        y='0.665'
      />
    </>
  ),
});

export function PricePlanRadio({ children, ...props }: PropsWithChildren<UseRadioProps>) {
  const { state, getInputProps, getRadioProps, htmlProps, getLabelProps } = useRadio(props);

  return (
    <chakra.label {...htmlProps} cursor='pointer' flex='1'>
      <input {...getInputProps({})} aria-label={props.value} />
      <Box
        {...getRadioProps()}
        bg={state.isChecked ? 'blue.50' : 'grey.50'}
        border={`${state.isChecked ? '1.33px' : '1px'} solid`}
        borderColor={state.isChecked ? 'blue.500' : 'grey.100'}
        borderRadius='6px'
        w={12}
        width='full'
        {...(state.isFocused && {
          outline: '1px solid',
          outlineColor: 'blue.500',
          outlineOffset: '2px',
        })}
        padding='10px 12px 12px'
        position='relative'
      >
        <Flex {...getLabelProps()} color={state.isChecked ? 'black' : 'gray.600'} flexDirection='column' gap={1}>
          {children}
        </Flex>
      </Box>
    </chakra.label>
  );
}

export type PricePlanRadioGroupProps = {
  onChange: (value: BillingFrequency) => void;
  value: BillingFrequency;
  children: ({ getRadioProps }: { getRadioProps: ReturnType<typeof useRadioGroup>['getRadioProps'] }) => ReactNode;
};

export function PricePlanRadioGroup({ children, onChange, value }: PricePlanRadioGroupProps) {
  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: 'all',
    onChange,
    value,
  });
  return (
    <Flex {...getRootProps()} flexDirection='column' gap={1}>
      {children({ getRadioProps })}
    </Flex>
  );
}

export function PlanCard({
  item,
  couponApplied,
  couponPercentOff,
  ...radioProps
}: {
  item: {
    label: string;
    value: BillingFrequency;
    savingsBlurb?: string;
    displayPrice?: string;
    displayPriceUnits?: string;
    strikeThroughDisplayPrice?: string | null;
  };
  couponApplied?: boolean;
  couponPercentOff?: number | null;
} & UseRadioProps) {
  function renderPrice() {
    if (!item.displayPrice)
      return (
        <>
          <Skeleton as='span' display='inline-block' height='29.5px' marginEnd={1} width='36px' />
          <Skeleton as='span' display='inline-block' height='16.5px' width='55px' />
        </>
      );
    else if (item.strikeThroughDisplayPrice) {
      return (
        <Flex
          alignItems='baseline'
          display='inline-flex'
          flex={1}
          gap='0px 4px'
          justifyContent={item.value === 'purchase' ? 'space-between' : 'flex-start'}
          wrap='wrap-reverse'
        >
          <Box as='ins' fontSize='1.375rem' fontWeight='600 !important' lineHeight='1.75rem' textDecoration='none'>
            {item.displayPrice}
            <Box as='span' color='grey.500' fontSize='0.8125rem' fontWeight='400 !important' lineHeight='1.4'>
              {item.displayPriceUnits}
            </Box>
          </Box>
          <Box
            as='del'
            color={radioProps.isChecked ? 'purple.500' : 'grey.200'}
            fontSize='1.125rem'
            fontWeight='500'
            lineHeight='1.75'
          >
            {item.strikeThroughDisplayPrice}
          </Box>
        </Flex>
      );
    } else {
      return (
        <Box as='span' fontSize='1.375rem' fontWeight='600 !important' lineHeight='1.75rem' textDecoration='none'>
          {item.displayPrice}
          <Box as='span' color='grey.500' fontSize='0.8125rem' fontWeight='400 !important' lineHeight='1.4'>
            {item.displayPriceUnits}
          </Box>
        </Box>
      );
    }
  }

  function renderSavingsTag() {
    const hasActiveCouponApplied = couponApplied && typeof couponPercentOff === 'number';
    const savingsBlurb =
      couponApplied && typeof couponPercentOff === 'number' ? `${couponPercentOff}% off` : item.savingsBlurb;
    if (!savingsBlurb) return null;
    const activeTagColor = hasActiveCouponApplied ? 'purple.500' : 'blue.500';
    return (
      <Tag
        bg={radioProps.isChecked ? activeTagColor : 'grey.200'}
        border='none'
        borderRadius='2px'
        boxShadow='none'
        color='white'
        fontFamily='condensed'
        fontSize='0.875rem'
        fontWeight='500'
        letterSpacing='0.06em'
        lineHeight='1'
        textTransform='uppercase'
        whiteSpace='nowrap'
      >
        <TagLabel>{savingsBlurb}</TagLabel>
      </Tag>
    );
  }

  return (
    <PricePlanRadio {...radioProps}>
      <Flex gap={2}>
        {radioProps.isChecked ? (
          <RadioCheckedIcon height='18px' position='relative' top='5px' width='18px' />
        ) : (
          <RadioUncheckedIcon height='18px' position='relative' top='5px' width='18px' />
        )}
        <Box flex='1'>
          <Flex alignItems='center' gap={4} justifyContent='space-between'>
            <Heading
              as={Text}
              color={radioProps.isChecked ? 'grey.900' : 'grey.500'}
              fontFamily='condensed'
              fontSize='0.9375rem'
              fontWeight='500'
              letterSpacing='0.06em'
              lineHeight='1'
              textTransform='uppercase'
              whiteSpace='nowrap'
            >
              {item.label} {item.value === '4month' && <Box as='sup'>1</Box>}
            </Heading>
            {renderSavingsTag()}
          </Flex>

          <Text
            color={radioProps.isChecked ? 'grey.900' : 'grey.500'}
            display='flex'
            fontFamily='body'
            whiteSpace='nowrap'
          >
            {renderPrice()}
          </Text>
        </Box>
      </Flex>
    </PricePlanRadio>
  );
}

export function PlanGroup({
  children,
  title,
  subtitle,
  description,
  isActive,
  onDetailsOpen,
}: PropsWithChildren<{
  title: string;
  subtitle: ReactNode;
  description: ReactNode;
  isActive: boolean;
  onDetailsOpen?: () => void;
}>) {
  const { isOpen: isDetailsOpen, onToggle: onDetailsToggle, onClose: onDetailsClose } = useDisclosure();

  useEffect(() => {
    if (!isActive) {
      onDetailsClose();
    }
  }, [isActive, onDetailsClose]);

  function handleDetailsToggle() {
    if (!isDetailsOpen) {
      onDetailsOpen?.();
    }
    onDetailsToggle();
  }

  return (
    <Card
      as='fieldset'
      bgColor='white'
      border='1.33px solid'
      borderColor={isActive ? 'blue.500' : 'transparent'}
      borderRadius='8px'
      boxShadow='none'
      display='flex'
      flexDirection='column'
      gap={1}
      overflow='hidden'
      padding={3}
    >
      <Box>
        <Box borderBottom='1px solid' borderColor='grey.200' mb={3} pb={3}>
          <Flex alignItems='center' justifyContent='space-between'>
            <Heading as='legend' fontSize='1.25rem' fontWeight='600' lineHeight='1.2'>
              {title}
            </Heading>
            <IconButton
              aria-label={`${isDetailsOpen ? 'Hide' : 'Show'} "${title}" details`}
              color='grey.900'
              colorScheme='white'
              flexShrink={0}
              fontSize='1.25rem'
              icon={isDetailsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              size='sm'
              variant='ghost'
              isRound
              onClick={handleDetailsToggle}
            />
          </Flex>
          {subtitle}
        </Box>
        <Collapse in={isDetailsOpen}>{description}</Collapse>
      </Box>
      {children}
    </Card>
  );
}
